import React from 'react';
import { Helmet } from 'react-helmet-async';
import { META_CONTENT, DEFAULT_META, PageMeta, INDEXABLE_PATHS } from '../../utils/seo/metaContent';

interface SEOHeadProps {
    page: string;
    customMeta?: Partial<PageMeta>;
}

/**
 * SEOHead Component
 * 
 * Dynamically sets meta tags for SEO optimization using react-helmet-async.
 * All meta changes are in the <head> and not visible to users in the page content.
 * 
 * Usage:
 * <SEOHead page="home" />
 * <SEOHead page="quoter" />
 * <SEOHead page="cruisingSails" customMeta={{ title: "Custom Title" }} />
 */
const SEOHead: React.FC<SEOHeadProps> = ({ page, customMeta = {} }) => {
    // Get page-specific meta or use default
    const pageMeta = META_CONTENT[page] || DEFAULT_META;
    
    // Merge with custom overrides if provided
    const meta: PageMeta = {
        ...pageMeta,
        ...customMeta
    };
    
    // Always use non-www domain for canonical URLs
    // Strip trailing slash unless it's the root path
    const pathname = window.location.pathname;
    const cleanPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
    const canonicalUrl = `https://pointsails.com${cleanPath}`;

    // Default to noindex. Only allowlisted content pages get indexed.
    // Static /sails/*.html pages have their own meta tags and are not affected.
    const shouldIndex = INDEXABLE_PATHS.includes(cleanPath.toLowerCase());
    const robotsContent = shouldIndex ? 'index, follow' : 'noindex, nofollow';
    
    // Default OG image if not specified (use hero image)
    const ogImage = meta.ogImage || 'https://pointsails.com/images/sugar-sue-desktop.webp';
    const ogImageWidth = meta.ogImageWidth || '1200';
    const ogImageHeight = meta.ogImageHeight || '630';
    
    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{meta.title}</title>
            <meta name="description" content={meta.description} />
            <meta name="keywords" content={meta.keywords} />
            <link rel="canonical" href={canonicalUrl} />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content={meta.ogType || 'website'} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={meta.title} />
            <meta property="og:description" content={meta.description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content={ogImageWidth} />
            <meta property="og:image:height" content={ogImageHeight} />
            <meta property="og:site_name" content="Point Sails" />
            <meta property="og:locale" content="en_US" />
            
            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={meta.title} />
            <meta name="twitter:description" content={meta.description} />
            <meta name="twitter:image" content={ogImage} />
            
            {/* Additional SEO */}
            <meta name="robots" content={robotsContent} />
            <meta name="googlebot" content={robotsContent} />
            <meta name="author" content="Point Sails" />
        </Helmet>
    );
};

export default SEOHead;
